import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Circle, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

const SALNES_CENTER = [42.5, -8.82];

const STATUS_COLOR = {
  NUEVA: "#E76F51",
  EN_TRIAGE: "#F4A261",
  ASIGNADA: "#2A9D8F",
  EN_RUTA: "#0B4C7A",
  RESUELTA: "#10B981",
  CERRADA: "#6B7280",
};

function ClusterLayer({ points, onSelect, showOverflow, showObservations, viewDetailLabel }) {
  const map = useMap();
  const groupRef = useRef(null);
  // Track blob URLs created inside popups so we can revoke them on unmount /
  // cluster refresh (Bearer-authenticated image fetching).
  const blobUrlsRef = useRef([]);

  const cleanupBlobs = () => {
    blobUrlsRef.current.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch { /* ignore */ }
    });
    blobUrlsRef.current = [];
  };

  useEffect(() => {
    if (!groupRef.current) {
      groupRef.current = L.markerClusterGroup({
        chunkedLoading: true,
        // iter45 · escala monocolor naranja y clusters más pequeños para
        // permitir ver cada marcador individual al hacer zoom in.
        maxClusterRadius: 40,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: 15,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          const bg =
            count < 10 ? "#FED8B1" :
            count < 30 ? "#FDBA74" :
            count < 100 ? "#FB923C" :
            count < 300 ? "#EA580C" :
                          "#9A3412";
          const fg = count < 30 ? "#7C2D12" : "#FFFFFF";
          const size = count < 20 ? 34 : count < 100 ? 42 : 50;
          return L.divIcon({
            html:
              `<div style="background:${bg};color:${fg};border:2px solid #fff;` +
              `border-radius:9999px;width:${size}px;height:${size}px;` +
              `display:flex;align-items:center;justify-content:center;` +
              `font-family:Inter,sans-serif;font-size:12px;font-weight:600;` +
              `box-shadow:0 2px 8px rgba(0,0,0,0.18)">${count}</div>`,
            className: "cluster-orange",
            iconSize: L.point(size, size, true),
          });
        },
      });
      map.addLayer(groupRef.current);
    }
    const g = groupRef.current;
    cleanupBlobs();
    g.clearLayers();

    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    const token =
      typeof window !== "undefined"
        ? window.sessionStorage?.getItem("salnes_token")
        : null;

    // Lazily fetch the anonymized image with JWT and swap it into the <img>.
    // Retries with backoff (2s, 5s, 10s) on 202/503 (processing) responses.
    const loadImage = async (objectId, imgEl) => {
      if (!imgEl || !objectId) return;
      const backoff = [0, 2000, 5000, 10000];
      let attempt = 0;
      let cancelled = false;
      imgEl.__cancelLoad = () => { cancelled = true; };
      while (attempt < backoff.length && !cancelled) {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, backoff[attempt]));
          if (cancelled) return;
        }
        try {
          const res = await fetch(
            `${apiBase}/api/incidencias/${objectId}/image`,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }
          );
          if (res.ok) {
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            blobUrlsRef.current.push(url);
            imgEl.src = url;
            imgEl.setAttribute("data-testid", "marker-popup-img-loaded");
            imgEl.style.display = "block";
            return;
          }
          if (res.status === 202 || res.status === 503) {
            imgEl.setAttribute("data-testid", "marker-popup-img-processing");
            imgEl.alt = "processing";
            imgEl.src =
              "data:image/svg+xml;utf8," +
              encodeURIComponent(
                `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='160' viewBox='0 0 260 160'><rect width='260' height='160' fill='#FEF3C7'/><text x='50%' y='48%' font-family='Inter,sans-serif' font-size='11' fill='#92400E' text-anchor='middle'>Procesando imaxe pola IA…</text><text x='50%' y='62%' font-family='Inter,sans-serif' font-size='9' fill='#B45309' text-anchor='middle'>Actualiza en uns segundos</text></svg>`
              );
            attempt += 1;
            continue;
          }
          if (res.status === 404) {
            imgEl.setAttribute("data-testid", "marker-popup-img-none");
            imgEl.src =
              "data:image/svg+xml;utf8," +
              encodeURIComponent(
                `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='160' viewBox='0 0 260 160'><rect width='260' height='160' fill='#F3F4F6'/><text x='50%' y='52%' font-family='Inter,sans-serif' font-size='11' fill='#9CA3AF' text-anchor='middle'>Sen imaxe dispoñible</text></svg>`
              );
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        } catch (e) {
          imgEl.setAttribute("data-testid", "marker-popup-img-error");
          imgEl.alt = "no-image";
          imgEl.src =
            "data:image/svg+xml;utf8," +
            encodeURIComponent(
              `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='160' viewBox='0 0 260 160'><rect width='260' height='160' fill='#F3F4F6'/><text x='50%' y='52%' font-family='Inter,sans-serif' font-size='11' fill='#9CA3AF' text-anchor='middle'>Erro cargando imaxe</text></svg>`
            );
          return;
        }
      }
      // Exhausted retries — keep processing state
    };

    (points || []).forEach((p) => {
      const isOverflow = p.overflow === true;
      if (isOverflow && !showOverflow) return;
      if (!isOverflow && !showObservations) return;
      const color = isOverflow
        ? "#E76F51"
        : STATUS_COLOR[p.status] || "#0B4C7A";
      const marker = L.circleMarker([p.lat, p.lon], {
        radius: isOverflow ? 8 : 6,
        color,
        weight: 2,
        fillColor: color,
        fillOpacity: 0.85,
      });

      // Build popup with a real DOM button so we can bind a click handler
      // and expose data-testids the tester requires.
      const popupEl = L.DomUtil.create("div");
      popupEl.setAttribute("data-testid", "marker-popup");
      popupEl.style.font = "12px Inter, sans-serif";
      popupEl.style.minWidth = "220px";
      popupEl.innerHTML = `
        <div style="font-family: 'Playfair Display', serif; font-size: 14px; color:#0B4C7A; font-weight:600">
          ${isOverflow ? "⚑ " : ""}#${p.id}
        </div>
        <div style="color:#6B7280; font-size:11px; margin: 4px 0 8px">
          ${p.status || "—"} · ${isOverflow ? "Overflow" : "Observación"}
        </div>
      `;

      // Placeholder image element — replaced by an authenticated blob-URL
      // once the popup is opened. Kept out of DOM until first open to avoid
      // useless network traffic on every render.
      const img = L.DomUtil.create("img", "", popupEl);
      img.setAttribute("data-testid", "marker-popup-img");
      img.setAttribute("data-object-id", String(p.id));
      img.style.cssText =
        "width:100%;max-width:240px;height:auto;border-radius:8px;background:#F4E4C1;display:block;margin-bottom:8px;";
      img.alt = "loading";
      // Initial neutral placeholder (SVG) so the popup has a solid box
      img.src =
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
          `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='160' viewBox='0 0 260 160'><rect width='260' height='160' fill='#F4E4C1'/><text x='50%' y='52%' font-family='Inter,sans-serif' font-size='11' fill='#6B7280' text-anchor='middle'>Cargando imaxe…</text></svg>`
        );

      const coordsEl = L.DomUtil.create("div", "", popupEl);
      coordsEl.style.cssText = "color:#6B7280; font-size:10px; margin-bottom: 8px";
      coordsEl.textContent = `${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}`;

      const btn = L.DomUtil.create("button", "", popupEl);
      btn.setAttribute("type", "button");
      btn.setAttribute("data-testid", "marker-detail-btn");
      btn.setAttribute("data-marker-id", String(p.id));
      btn.textContent = viewDetailLabel;
      btn.style.cssText =
        "background:#0B4C7A;color:#fff;border:none;border-radius:9999px;padding:6px 14px;font-size:11px;cursor:pointer;font-family:Inter,sans-serif;letter-spacing:0.01em;";
      L.DomEvent.on(btn, "click", (ev) => {
        L.DomEvent.stopPropagation(ev);
        marker.closePopup();
        onSelect?.(p);
      });

      marker.bindPopup(popupEl, { closeButton: true, autoPan: true, minWidth: 240 });
      let imgLoaded = false;
      marker.on("popupopen", () => {
        if (!imgLoaded) {
          imgLoaded = true;
          loadImage(p.id, img);
        }
      });
      marker.on("click", () => marker.openPopup());
      marker.bindTooltip(
        `<div style="font: 11px Inter, sans-serif"><b>#${p.id}</b> · ${p.status || ""}</div>`,
        { direction: "top", offset: [0, -6] }
      );
      g.addLayer(marker);
    });

    return () => {
      cleanupBlobs();
    };
  }, [points, onSelect, map, showOverflow, showObservations, viewDetailLabel]);

  return null;
}

function ConcelloRings({ concellos, visible }) {
  if (!visible) return null;
  return (
    <>
      {concellos.map((c) => (
        <Circle
          key={c.key}
          center={c.center}
          radius={2500}
          pathOptions={{
            color: c.color,
            weight: 1,
            fillColor: c.color,
            fillOpacity: 0.05,
            dashArray: "4 4",
          }}
        >
          <Tooltip direction="center" opacity={0.9} permanent={false}>
            <span className="font-serif">{c.name}</span>
          </Tooltip>
        </Circle>
      ))}
    </>
  );
}

function SigeusPointsLayer({ points, visible, viewDetailLabel }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    if (!groupRef.current) {
      groupRef.current = L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 55,
        showCoverageOnHover: false,
        disableClusteringAtZoom: 16,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          const size = count < 20 ? 32 : count < 100 ? 40 : 48;
          return L.divIcon({
            html: `<div style="background:#6B7280;color:#fff;border:2px solid #fff;border-radius:9999px;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;font-family:Inter,sans-serif;font-size:11px;font-weight:600;opacity:0.85;box-shadow:0 2px 8px rgba(0,0,0,0.15)">${count}</div>`,
            className: "sigeus-cluster",
            iconSize: [size, size],
          });
        },
      });
    }
    const g = groupRef.current;
    g.clearLayers();
    if (!visible) {
      if (map.hasLayer(g)) map.removeLayer(g);
      return;
    }
    (points || []).forEach((p) => {
      const lat = p?.latitude, lon = p?.longitude;
      if (lat == null || lon == null) return;
      const marker = L.circleMarker([lat, lon], {
        radius: 5,
        color: "#374151",
        weight: 1,
        fillColor: "#6B7280",
        fillOpacity: 0.7,
      });
      const html = `
        <div style="font:12px Inter,sans-serif;min-width:200px">
          <div style="font-family:'Playfair Display',serif;font-size:13px;color:#0B4C7A;font-weight:600">
            #${p.col_point_id} · siGEUS
          </div>
          <div style="color:#6B7280;font-size:11px;margin:3px 0">
            ${(p.address || "").replace(/</g, "&lt;")}
          </div>
          <div style="color:#6B7280;font-size:10px">
            ${p.concello_asignado || "—"} · ${p.postal_code || ""}
          </div>
        </div>`;
      const popupEl = L.DomUtil.create("div");
      popupEl.setAttribute("data-testid", "sigeus-popup");
      popupEl.innerHTML = html;
      const btn = L.DomUtil.create("a", "", popupEl);
      btn.setAttribute("href", `/app/contedores?point=${p.col_point_id}`);
      btn.setAttribute("data-testid", `sigeus-point-detail-${p.col_point_id}`);
      btn.textContent = viewDetailLabel;
      btn.style.cssText =
        "display:inline-block;margin-top:8px;background:#0B4C7A;color:#fff;padding:5px 12px;border-radius:9999px;font-size:11px;text-decoration:none;font-family:Inter,sans-serif;";
      marker.bindPopup(popupEl, { closeButton: true, autoPan: true, minWidth: 220 });
      marker.bindTooltip(
        `<div style="font:11px Inter,sans-serif"><b>siGEUS #${p.col_point_id}</b></div>`,
        { direction: "top", offset: [0, -6] }
      );
      g.addLayer(marker);
    });
    if (!map.hasLayer(g)) map.addLayer(g);
    return () => {
      if (map.hasLayer(g)) map.removeLayer(g);
    };
  }, [points, visible, map, viewDetailLabel]);

  return null;
}

function VehicleLayer({ vehicles, visible }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    if (!groupRef.current) {
      groupRef.current = L.layerGroup();
    }
    const g = groupRef.current;
    g.clearLayers();
    if (!visible) {
      if (map.hasLayer(g)) map.removeLayer(g);
      return;
    }
    (vehicles || []).forEach((v) => {
      const lat = v?.last_position?.latitude;
      const lon = v?.last_position?.longitude;
      if (lat == null || lon == null) return;
      const status = (v.gps_status || v.status || "").toLowerCase();
      const color = status === "moving" ? "#10B981" : status === "idle" ? "#0B4C7A" : "#6B7280";
      const marker = L.circleMarker([lat, lon], {
        radius: 9,
        color: "#FFFFFF",
        weight: 2,
        fillColor: color,
        fillOpacity: 0.95,
      });
      const html = `
        <div style="font: 12px Inter, sans-serif; min-width: 180px">
          <div style="font-family:'Playfair Display',serif;font-size:14px;color:#0B4C7A;font-weight:600">
            🚚 ${v.plate || v.id}
          </div>
          <div style="color:#6B7280;font-size:11px;margin:4px 0">
            ${v.source || v.gps_source || "—"} · ${v.gps_status || v.status || "—"}
          </div>
          <div style="color:#6B7280;font-size:10px">
            ${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}
          </div>
        </div>`;
      marker.bindPopup(html, { closeButton: true, autoPan: true, minWidth: 200 });
      marker.bindTooltip(
        `<div style="font: 11px Inter, sans-serif"><b>${v.plate || v.id}</b> · ${v.gps_status || ""}</div>`,
        { direction: "top", offset: [0, -6] }
      );
      marker.__plate = v.plate || v.id;
      const el = marker.getElement?.();
      if (el) {
        el.setAttribute("data-testid", `geoportal-vehicle-marker-${v.plate || v.id}`);
      }
      g.addLayer(marker);
    });
    if (!map.hasLayer(g)) map.addLayer(g);
    // Set data-testid AFTER the marker path is rendered in the SVG pane.
    g.eachLayer((mk) => {
      const el = mk.getElement?.();
      const plate = mk.__plate;
      if (el && plate) {
        el.setAttribute("data-testid", `geoportal-vehicle-marker-${plate}`);
      }
    });
    return () => {
      if (map.hasLayer(g)) map.removeLayer(g);
    };
  }, [vehicles, visible, map]);

  return null;
}

// iter45 · Capa de pesajes RSU sobre el mapa. Cluster con paleta naranja.
const WASTE_COLOR = {
  papel: "#0B4C7A",
  vidrio: "#10B981",
  envases: "#F59E0B",
  organico: "#A16207",
  organica: "#A16207",
  resto: "#6B7280",
};
function WeighinsLayer({ points, visible, mode = "aggregated" }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    if (!groupRef.current) {
      groupRef.current = L.markerClusterGroup({
        chunkedLoading: true,
        maxClusterRadius: 40,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        disableClusteringAtZoom: 15,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          const bg =
            count < 10 ? "#FED8B1" :
            count < 30 ? "#FDBA74" :
            count < 100 ? "#FB923C" :
            count < 300 ? "#EA580C" :
                          "#9A3412";
          const fg = count < 30 ? "#7C2D12" : "#FFFFFF";
          const size = count < 20 ? 34 : count < 100 ? 42 : 50;
          return L.divIcon({
            html:
              `<div style="background:${bg};color:${fg};border:2px solid #fff;` +
              `border-radius:9999px;width:${size}px;height:${size}px;` +
              `display:flex;align-items:center;justify-content:center;` +
              `font-family:Inter,sans-serif;font-size:12px;font-weight:600;` +
              `box-shadow:0 2px 8px rgba(0,0,0,0.18)">${count}</div>`,
            className: "cluster-orange",
            iconSize: L.point(size, size, true),
          });
        },
      });
    }
    const g = groupRef.current;
    g.clearLayers();
    if (!visible) {
      if (map.hasLayer(g)) map.removeLayer(g);
      return;
    }

    // iter48 · Escala logarítmica para radio del marker aggregated en función
    //   del peso total. Rango 6–20 px. Los `events` mantienen radio fijo.
    const totals = (points || [])
      .map((p) => p.weight_total_kg || 0)
      .filter((v) => v > 0);
    const maxLog = totals.length ? Math.log10(Math.max(...totals) + 1) : 1;

    (points || []).forEach((p) => {
      if (p.lat == null || p.lon == null) return;
      const wt = String(p.waste_type_dominant || p.waste_type || "").toLowerCase();
      const color = WASTE_COLOR[wt] || "#0B4C7A";

      let radius = 5;
      if (mode === "aggregated") {
        const val = p.weight_total_kg || 0;
        const norm = val > 0 ? Math.log10(val + 1) / maxLog : 0;
        radius = Math.round(6 + norm * 14); // 6..20
      }

      const marker = L.circleMarker([p.lat, p.lon], {
        radius,
        color: "#FFFFFF",
        weight: 1.5,
        fillColor: color,
        fillOpacity: 0.85,
      });

      let html;
      if (mode === "aggregated") {
        const kg = p.weight_total_kg != null
          ? Number(p.weight_total_kg).toLocaleString("es-ES", { maximumFractionDigits: 1 })
          : "—";
        const count = p.weighins_count ?? "—";
        const last = p.last_weighin_date
          ? String(p.last_weighin_date).slice(0, 10)
          : "—";
        const cp = p.col_point_id
          ? `#${p.col_point_id}`
          : "—";
        html = `
          <div style="font:12px Inter,sans-serif;min-width:230px" data-testid="weighin-agg-popup">
            <div style="font-family:'Playfair Display',serif;font-size:13px;color:#0B4C7A;font-weight:600">
              Contenedor ${cp} · ${p.municipality || "—"}
            </div>
            <div style="color:#0B4C7A;font-weight:600;margin-top:6px;font-size:13px">
              ⚖ ${kg} kg  ·  ${count} pesajes
            </div>
            <div style="color:#6B7280;font-size:11px;margin-top:2px">Último: ${last}</div>
            <div style="color:#6B7280;font-size:11px;margin-top:4px">
              <span style="display:inline-block;width:8px;height:8px;background:${color};border-radius:9999px;margin-right:4px;vertical-align:middle"></span>
              Tipo dominante: ${p.waste_type_dominant || "—"}
            </div>
          </div>`;
      } else {
        const kg = p.weight_kg != null ? Number(p.weight_kg).toFixed(1) : "—";
        const date = p.weighing_date
          ? String(p.weighing_date).slice(0, 16).replace("T", " ")
          : "—";
        html = `
          <div style="font:12px Inter,sans-serif;min-width:200px" data-testid="weighin-popup">
            <div style="font-family:'Playfair Display',serif;font-size:13px;color:#0B4C7A;font-weight:600">
              ⚖ ${kg} kg · ${p.waste_type || "—"}
            </div>
            <div style="color:#6B7280;font-size:11px;margin-top:4px">${date}</div>
            <div style="color:#6B7280;font-size:11px">${p.municipality || "—"}</div>
            <div style="color:#6B7280;font-size:10px;margin-top:4px">
              ${p.vehicle_plate ? "🚚 " + p.vehicle_plate : ""}
            </div>
          </div>`;
      }
      marker.bindPopup(html, { closeButton: true, autoPan: true, minWidth: 240 });
      g.addLayer(marker);
    });
    if (!map.hasLayer(g)) map.addLayer(g);
    return () => {
      if (map.hasLayer(g)) map.removeLayer(g);
    };
  }, [points, visible, mode, map]);

  return null;
}

export default function GeoMap({
  points = [],
  concellos = [],
  vehicles = [],
  sigeusPoints = [],
  weighins = [],
  weighinsMode = "aggregated",
  layers,
  basemap = "carto",
  onSelect,
  viewDetailLabel = "Ver detalle",
  sigeusDetailLabel = "Ver detalle en Contenedores",
}) {
  const tileUrl = useMemo(() => {
    if (basemap === "pnoa") {
      // iter45 · Ortofoto PNOA IGN España (WMTS oficial). Cobertura completa
      // en Galicia. `subdomains` no aplica — es un solo endpoint.
      return "https://www.ign.es/wmts/pnoa-ma?layer=OI.OrthoimageCoverage&style=default&tilematrixset=GoogleMapsCompatible&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fjpeg&TileMatrix={z}&TileCol={x}&TileRow={y}";
    }
    if (basemap === "esri") {
      return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}";
    }
    return "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
  }, [basemap]);
  const attribution =
    basemap === "pnoa"
      ? "© Instituto Geográfico Nacional de España · PNOA"
      : basemap === "esri"
        ? "&copy; Esri &mdash; National Geographic, DeLorme, NAVTEQ"
        : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · <a href="https://carto.com/attributions">CARTO</a>';
  const maxZoomForBasemap = basemap === "pnoa" ? 19 : 19;

  return (
    <div className="relative h-full w-full" data-testid="geo-map">
      <MapContainer
        center={SALNES_CENTER}
        zoom={11}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          key={basemap}
          attribution={attribution}
          url={tileUrl}
          maxZoom={maxZoomForBasemap}
        />
        <ConcelloRings concellos={concellos} visible={layers.concellos} />
        {layers.detections && (
          <ClusterLayer
            points={points}
            onSelect={onSelect}
            showOverflow={layers.overflow}
            showObservations={layers.observations}
            viewDetailLabel={viewDetailLabel}
          />
        )}
        <VehicleLayer vehicles={vehicles} visible={layers.vehicles} />
        <SigeusPointsLayer
          points={sigeusPoints}
          visible={!!layers.sigeus_points}
          viewDetailLabel={sigeusDetailLabel}
        />
        <WeighinsLayer
          points={weighins}
          visible={!!layers.weighins}
          mode={weighinsMode}
        />
      </MapContainer>
    </div>
  );
}
